/**
 * Shared S3 object-key and public-URL helpers.
 *
 * ## AWS conventions we follow
 *
 * - **Virtual-hosted–style regional URLs**
 *   `https://{bucket}.s3.{region}.amazonaws.com/{key}`
 *   See: https://docs.aws.amazon.com/AmazonS3/latest/userguide/VirtualHosting.html
 *
 * - **URL-encode each key path segment** (keep `/` as separators).
 *   Spaces → `%20`, literal `+` → `%2B`, etc.
 *   See: https://docs.aws.amazon.com/AmazonS3/latest/developerguide/s3_example_s3_Scenario_URIParsing_section.html
 *
 * - **Treat `key` as source of truth.** Persist the raw object key; derive `url`
 *   with {@link buildS3ObjectUrl} (or {@link resolveStoredMediaFileUrl}). Never
 *   concatenate `bucket + "/" + key` without encoding, and avoid legacy
 *   `https://{bucket}.s3.amazonaws.com/...` (no region).
 *
 * - **Prefer safe key segments.** Keep the human filename in DB/`name`; use
 *   {@link sanitizeS3FileName} for the object-key basename so spaces/`+`/accents
 *   never land in the key.
 *
 * Uploads via SDK presigned URLs already encode correctly. Bugs appear when apps
 * later invent a public URL from the raw key — that is what these helpers prevent.
 */

export const DEFAULT_S3_REGION = "eu-west-3" as const;

export type BuildS3ObjectUrlOptions = {
  bucket: string;
  /** Raw S3 object key (unencoded). Leading slashes are stripped. */
  key: string;
  /** AWS region of the bucket. Defaults to {@link DEFAULT_S3_REGION}. */
  region?: string;
};

export type ResolveStoredMediaFileUrlOptions = {
  bucket: string;
  region?: string;
};

/**
 * Percent-encode each S3 key path segment while preserving `/` separators.
 *
 * @example
 * encodeS3ObjectKey("delivery/11695/docs/_0_Pv + feuille.pdf")
 * // → "delivery/11695/docs/_0_Pv%20%2B%20feuille.pdf"
 */
export function encodeS3ObjectKey(key: string): string {
  return key
    .replace(/^\/+/, "")
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

/**
 * Build a public virtual-hosted–style S3 object URL for a raw object key.
 *
 * @throws if `bucket` or `key` is empty
 */
export function buildS3ObjectUrl({
  bucket,
  key,
  region = DEFAULT_S3_REGION,
}: BuildS3ObjectUrlOptions): string {
  const trimmedBucket = bucket.trim();
  const trimmedKey = key.replace(/^\/+/, "").trim();
  if (!trimmedBucket) {
    throw new Error("S3 bucket is required to build an object URL");
  }
  if (!trimmedKey) {
    throw new Error("S3 object key is required to build an object URL");
  }

  return `https://${trimmedBucket}.s3.${region}.amazonaws.com/${encodeS3ObjectKey(trimmedKey)}`;
}

/**
 * Resolve a usable download URL for a stored media JSON entry.
 *
 * Prefer rebuilding from `key` (correct encoding + region). Fall back to a
 * stored `url` only when `key` is missing — useful for legacy rows.
 */
export function resolveStoredMediaFileUrl(
  file: { key?: string | null; url?: string | null },
  options: ResolveStoredMediaFileUrlOptions
): string | undefined {
  const key = file.key?.replace(/^\/+/, "").trim();
  if (key) {
    return buildS3ObjectUrl({
      bucket: options.bucket,
      key,
      region: options.region,
    });
  }

  const url = file.url?.trim();
  return url ? url : undefined;
}

/**
 * Sanitize a display filename for use as a single S3 key path segment.
 *
 * Preserves the extension, strips diacritics, and replaces unsafe characters
 * with `_`. The original name should still be stored separately as `name`.
 */
export function sanitizeS3FileName(fileName: string, maxLength = 100): string {
  const trimmed = fileName.trim();
  const lastDot = trimmed.lastIndexOf(".");
  const hasExt = lastDot > 0 && lastDot < trimmed.length - 1;
  const extension = hasExt ? trimmed.slice(lastDot).toLowerCase() : "";
  const base = hasExt ? trimmed.slice(0, lastDot) : trimmed;

  let cleanName = base
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^[_./]+|[_./]+$/g, "");

  if (!cleanName) {
    cleanName = "file";
  }

  const maxBase = Math.max(1, maxLength - extension.length);
  if (cleanName.length > maxBase) {
    cleanName = cleanName.slice(0, maxBase);
  }

  return `${cleanName}${extension}`;
}

/**
 * Build a multi-file upload key segment: `_{index}_{sanitizedOriginalName}`.
 */
export function buildIndexedS3FileName(index: number, originalFileName: string): string {
  return `_${index}_${sanitizeS3FileName(originalFileName)}`;
}
